import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Check, Package, Truck, Star, ExternalLink, Loader2 } from "lucide-react";
import { API, http, usd } from "@/lib/api";
import { Logo } from "@/components/brand/Logo";
import { ReviewForm } from "@/components/site/ReviewForm";

const Step = ({ done, icon: Icon, label, sub, testId }) => (
  <li data-testid={testId} className="flex gap-4">
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${done ? "bg-[#C85A32] text-[#FDFBF7]" : "border border-[#D9CFC3] text-[#6E6661]"}`}>
      {done ? <Check size={18} /> : <Icon size={18} />}
    </span>
    <div>
      <p className="font-medium">{label}</p>
      {sub && <div className="mt-0.5 text-sm text-[#6E6661]">{sub}</div>}
    </div>
  </li>
);

export default function OrderPage() {
  const { token } = useParams();
  const [o, setO] = useState(null);
  const [state, setState] = useState("loading");

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/orders/${token}`);
      setO(data);
      setState("ok");
    } catch {
      setState("error");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const shipped = o?.fulfillment_status === "shipped";
  return (
    <main data-testid="order-page" className="grain relative min-h-screen bg-[#F2EAE1]">
      <div className="relative mx-auto max-w-3xl px-5 py-10 sm:px-8">
        <Logo />
        {state === "loading" && <Loader2 className="mt-32 animate-spin text-[#C85A32]" />}
        {state === "error" && (
          <div data-testid="order-not-found" className="mt-32">
            <p className="font-display text-3xl font-light">We couldn't find that order.</p>
            <p className="mt-3 text-[#4A4543]">Please use the link from your confirmation email, or reply to it and we'll help.</p>
          </div>
        )}
        {state === "ok" && o && (
          <div className="mt-16">
            <p className="eyebrow text-[#6E6661]">Order <span data-testid="order-page-ref" className="text-[#181615]">{o.order_ref}</span></p>
            <h1 className="font-display mt-3 text-5xl font-light leading-none tracking-tight">
              {shipped ? <>On its <span className="italic text-[#C85A32]">way.</span></> : <>Getting it <span className="italic text-[#C85A32]">ready.</span></>}
            </h1>
            <ol className="mt-10 space-y-6 rounded-[28px] border border-[#E3D9CD] bg-[#FDFBF7] p-7">
              <Step testId="order-step-paid" done icon={Check} label="Order confirmed" sub={o.paid_at ? new Date(o.paid_at).toLocaleDateString() : null} />
              <Step
                testId="order-step-shipped"
                done={shipped}
                icon={Truck}
                label={shipped ? `Shipped with ${o.carrier}` : "Preparing to ship from Alton, IL"}
                sub={shipped ? (
                  <span className="flex flex-wrap items-center gap-2">
                    Tracking <span data-testid="order-tracking-number" className="font-mono-ghp text-[#181615]">{o.tracking_number}</span>
                    {o.tracking_url && (
                      <a data-testid="order-tracking-link" href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C85A32] underline">Track package <ExternalLink size={12} /></a>
                    )}
                  </span>
                ) : "Usually within 1–2 business days"}
              />
              <Step testId="order-step-delivered" done={false} icon={Package} label="Delivered" sub="Your carrier will confirm delivery" />
            </ol>
            <div className="mt-6 flex flex-col items-start justify-between gap-5 rounded-[28px] bg-[#181615] p-7 text-[#F2EAE1] sm:flex-row sm:items-center">
              <div>
                <p className="eyebrow text-[#E0A030]">Your complimentary gift</p>
                <p className="font-display mt-2 text-2xl italic">The Recipe Almanac</p>
              </div>
              <a data-testid="order-download-pdf-button" href={`${API}/orders/${token}/recipes`} className="inline-flex items-center gap-2 rounded-full bg-[#E0A030] px-6 py-3.5 font-semibold text-[#181615] hover:bg-[#F2EAE1]">
                <Download size={16} /> Download PDF
              </a>
            </div>
            <dl className="mt-6 space-y-2 rounded-[28px] border border-[#E3D9CD] bg-[#FDFBF7] p-7 text-sm">
              <div className="flex justify-between"><dt>The Slowpour 01 × {o.quantity}</dt><dd>{usd(o.subtotal)}</dd></div>
              <div className="flex justify-between"><dt>Shipping</dt><dd>{usd(o.shipping)}</dd></div>
              <div className="flex justify-between"><dt>Sales tax</dt><dd>{usd(o.tax)}</dd></div>
              <div className="flex justify-between border-t border-[#E3D9CD] pt-3 font-semibold"><dt>Total</dt><dd>{usd(o.total)}</dd></div>
            </dl>
            <div className="mt-6">
              {o.has_review ? (
                <div data-testid="review-thanks" className="flex items-center gap-3 rounded-[28px] border border-[#E3D9CD] bg-[#FDFBF7] p-7">
                  <Star className="fill-[#E0A030] text-[#E0A030]" />
                  <p>Thanks for your review! It'll appear on the site once approved.</p>
                </div>
              ) : (
                <ReviewForm token={token} defaultName={o.customer_name ? `${o.customer_name.split(" ")[0]} ${(o.customer_name.split(" ")[1] || "").charAt(0)}${o.customer_name.split(" ")[1] ? "." : ""}`.trim() : ""} onDone={load} />
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
